import { Comment01Icon } from 'hugeicons-react';
import { useState, useRef, useEffect, useMemo } from 'react';
import '../../../Styles/Layout/Buttons.css';
import '../../../Styles/Sidebar/Buttons/HistoryButton.css';
import { Link, useNavigate } from 'react-router-dom';
import { useChatContext } from '../../../contexts/AppDataContext';
import { useUnifiedData } from '../../../contexts/AppDataContext';

export const HistoryButton = () => {
  const { currentChatId } = useChatContext();
  const { chats } = useUnifiedData();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const DISPLAY_LIMIT = 40;

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !(dropdownRef.current as any).contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleChatClick = (chatId: string) => {
    setIsOpen(false);
    navigate(`/${chatId}`);
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (chats?.length > 0) {
      setTimeout(() => {
        setIsOpen(true);
      }, 100);
    }
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 300);
  };

  const handleMouseLeaveQuick = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  // Group chats by time periods like in ChatList
  const categorizedChats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    
    const sortedChats = [...(chats || [])]
      .sort((a, b) => {
        const dateA = new Date(a.lastModified);
        const dateB = new Date(b.lastModified);
        return dateB.getTime() - dateA.getTime();
      })
      .slice(0, DISPLAY_LIMIT);

    return sortedChats.reduce<{
      today: typeof chats;
      yesterday: typeof chats;
      lastWeek: typeof chats;
      older: typeof chats;
    }>((acc, chat) => {
      const chatDate = new Date(chat.lastModified);
      if (chatDate >= today) {
        acc.today.push(chat);
      } else if (chatDate >= yesterday) {
        acc.yesterday.push(chat);
      } else if (chatDate >= lastWeek) {
        acc.lastWeek.push(chat);
      } else {
        acc.older.push(chat);
      }
      return acc;
    }, { today: [], yesterday: [], lastWeek: [], older: [] });
  }, [chats]);

  return (
    <div 
      className="history-button-container" 
      ref={dropdownRef}
    >
      <button
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Comment01Icon size={18} />
      </button>
      {isOpen && Array.isArray(chats) && (
        <div 
          className={`history-dropdown ${isOpen ? 'open' : 'closing'}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeaveQuick}
        >
          <div className="history-dropdown-header">
            <span>Recent Chats</span>
          </div>
          <div className="history-dropdown-content">
            {(categorizedChats.today.length + categorizedChats.yesterday.length + categorizedChats.lastWeek.length + categorizedChats.older.length) === 0 ? (
              <div className="no-chats-message">
                No chats yet. <Link to="/new">Start one!</Link>
              </div>
            ) : (
              <>
                {categorizedChats.today.length > 0 && (
                  <>
                    <div className="history-section-header">Today</div>
                    {categorizedChats.today.map((chat) => (
                      <div
                        key={chat.id}
                        className={`chat-option ${chat.id === currentChatId ? 'active' : ''}`}
                        onClick={() => handleChatClick(chat.id)}
                      >
                        <div className="history-chat-title">
                          {chat.title || 'New Chat'}
                        </div>
                      </div>
                    ))}
                  </>
                )}
                {categorizedChats.yesterday.length > 0 && (
                  <>
                    <div className="history-section-header">Yesterday</div>
                    {categorizedChats.yesterday.map((chat) => (
                      <div
                        key={chat.id}
                        className={`chat-option ${chat.id === currentChatId ? 'active' : ''}`}
                        onClick={() => handleChatClick(chat.id)}
                      >
                        <div className="history-chat-title">
                          {chat.title || 'New Chat'}
                        </div>
                      </div>
                    ))}
                  </>
                )}
                {categorizedChats.lastWeek.length > 0 && (
                  <>
                    <div className="history-section-header">Last 7 Days</div>
                    {categorizedChats.lastWeek.map((chat) => (
                      <div
                        key={chat.id}
                        className={`chat-option ${chat.id === currentChatId ? 'active' : ''}`}
                        onClick={() => handleChatClick(chat.id)}
                      >
                        <div className="history-chat-title">
                          {chat.title || 'New Chat'}
                        </div>
                      </div>
                    ))}
                  </>
                )}
                {categorizedChats.older.length > 0 && (
                  <>
                    <div className="history-section-header">Older</div>
                    {categorizedChats.older.map((chat) => (
                      <div
                        key={chat.id}
                        className={`chat-option ${chat.id === currentChatId ? 'active' : ''}`}
                        onClick={() => handleChatClick(chat.id)}
                      >
                        <div className="history-chat-title">
                          {chat.title || 'New Chat'}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoryButton;